/**
 * User type differentiation:
 * - platform_staff: Internal users (owners, managers, staff, drivers) - can access store-admin
 * - customer: B2C and B2B shoppers - can only access storefront
 * B2B status is determined by ResellerProfile existence, not by userType.
 */
export type UserType = 'platform_staff' | 'customer';

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  /** User type: platform_staff or customer */
  userType?: UserType;
  /** @deprecated Use roles[].systemKey or roleSystemKeys instead. Kept for backward compatibility. */
  roleSystemKey?: SystemRoleKey;
  locationId?: string;
  /** Default location ID for POS and other location-specific features */
  defaultLocationId?: string;
  location?: Location;
  organizationId?: string;
  /** @deprecated Use roleIds for multi-role support */
  roleId?: string;
  /** @deprecated Use roles array for multi-role support */
  role?: Role;
  /** Multi-role: Array of assigned role IDs */
  roleIds?: string[];
  /** Multi-role: Array of assigned roles */
  roles?: Role[];
  /** Multi-role: Array of role system keys from all assigned roles */
  roleSystemKeys?: string[];
  permissions?: string[]; // Array of "resource:action" strings
  locationIds?: string[]; // Array of accessible location IDs
  userLocations?: UserLocation[];
  /** Multi-role: User role junction records */
  userRoles?: UserRoleRecord[];
  /** Platform-level super admin flag - has access across all organizations */
  isSuperAdmin?: boolean;
  /** Admin flag - true if ANY assigned role has isAdmin=true */
  isAdmin?: boolean;
  // Default address fields
  defaultStreet?: string;
  defaultCity?: string;
  defaultState?: string;
  defaultPostalCode?: string;
  defaultCountry?: string;
  // Geolocation fields
  latitude?: number;
  longitude?: number;
  // Delivery preferences
  preferDelivery?: boolean;
  preferPickup?: boolean;
  deliveryInstructions?: string;
  // Reservation count (for customer list)
  reservations?: { id: string }[];
  createdAt: string;
  updatedAt: string;
}

/** System role keys - both legacy and new granular roles */
export type SystemRoleKey =
  | 'owner'         // Level 0 (Owner) - Platform owner (protected, can't be edited)
  | 'org_owner'     // Level 0 (Owner) - Organization owner
  | 'org_admin'     // Level 1 (Admin) - Administrative oversight
  | 'store_manager' // Level 2 (Manager) - Operational management
  | 'store_staff'   // Level 3 (Staff) - Basic operations
  | 'store_owner'   // Legacy
  | 'sales_operator'
  | 'inventory_manager'
  | 'b2b_manager'
  | 'delivery_handler'
  | 'customer'
  | 'super_admin'
  | 'warehouse_manager'
  | 'driver';

/** Role category for grouping and UI organization */
export type RoleCategory = 'administrative' | 'operational' | 'combined';

/** Junction record linking user to role */
export interface UserRoleRecord {
  id: string;
  userId: string;
  roleId: string;
  role?: Role;
  createdAt: string;
}

export interface AuthData {
  access_token: string;
  refresh_token: string;
  user: User;
  permissions?: string[]; // Can also come at top level
  locationIds?: string[];
}

export interface ProductImage {
  id: string;
  thumbnail: string;
  medium: string;
  large: string;
  originalName: string;
  size: number;
  order: number;
  createdAt: string;
}

export interface Product {
  id: string;
  name: string;
  description: string;
  sku: string;
  barcode?: string;
  alternateBarcodes?: string[]; // Phase 2: Barcode Scanning
  price: number;
  cost?: number;
  stockQuantity: number;
  lowStockThreshold: number;
  category?: string;
  images?: ProductImage[];
  isActive: boolean;
  isVisibleInStorefront: boolean;
  productType?: 'sellable' | 'internal'; // Phase 1.1
  defaultCostPrice?: number; // Weighted average cost
  trackBatches?: boolean;
  locationId: string;
  location?: Location;
  variants?: ProductVariant[];
  createdAt: string;
  updatedAt: string;
}

/** Product variant for managing size/color combinations with specific pricing and images */
export interface ProductVariant {
  id: string;
  organizationId: string;
  productId: string;
  product?: Product;
  sku: string;
  /** Variant attributes (e.g., { size: "M", color: "Red" }) */
  attributes: Record<string, unknown>;
  /** Price adjustment to add/subtract from base product price */
  priceAdjustment: number;
  /** Variant-specific images */
  images?: ProductImage[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Query parameters for variant matrix view */
export interface VariantMatrixQuery {
  rows: string;
  cols: string;
}

/** Single cell in the variant matrix */
export interface VariantMatrixCell {
  id?: string;
  sku?: string;
  priceAdjustment?: number;
  isActive?: boolean;
  attributes?: Record<string, unknown>;
  images?: ProductImage[];
  totalStock?: number;
  exists?: boolean;
}

/** Variant matrix response structure organizing variants into a grid */
export interface VariantMatrixResponse {
  productId: string;
  rowAttribute: string;
  rowValues: string[];
  columnAttribute: string;
  columnValues: string[];
  /** Matrix of variant data. Structure: cells[rowIndex][columnIndex] */
  cells: VariantMatrixCell[][];
  totalVariants: number;
  totalCombinations: number;
}

/** Bulk variant creation request */
export interface BulkCreateVariantsDto {
  attributes: Record<string, string[]>;
  skuTemplate: string;
}

/** Bulk variant update request */
export interface BulkUpdateVariantsDto {
  updates: {
    id: string;
    sku?: string;
    attributes?: Record<string, unknown>;
    priceAdjustment?: number;
    images?: ProductImage[];
    isActive?: boolean;
  }[];
}

export interface Reservation {
  id: string;
  customerId: string;
  customer?: User;
  locationId: string;
  location?: Location;
  items: ReservationItem[];
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled';
  fulfillmentMethod: 'pickup' | 'delivery';
  deliveryAddress?: {
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    notes?: string;
  };
  totalAmount: number;
  notes?: string;
  reservedUntil: string;
  createdAt: string;
  updatedAt: string;
}

export interface ReservationItem {
  id: string;
  reservationId: string;
  productId: string;
  product?: Product;
  quantity: number;
  price: number;
}

export type SaleType = 'b2c' | 'b2b_direct' | 'b2b_quote';

// ============================================
// Order Types (Unified Order System)
// ============================================

export enum OrderType {
  B2C = 'b2c',
  B2B_DIRECT = 'b2b_direct',
  B2B_QUOTE = 'b2b_quote',
}

export enum OrderPaymentStatus {
  UNPAID = 'unpaid',
  PARTIALLY_PAID = 'partially_paid',
  PAID = 'paid',
  REFUNDED_PARTIAL = 'refunded_partial',
  REFUNDED_FULL = 'refunded_full',
}

export enum OrderFulfillmentStatus {
  PENDING = 'pending',
  WAITING_TRANSFER = 'waiting_transfer',
  TRANSFER_ISSUE = 'transfer_issue',
  WAITING_ADMIN_ACTION = 'waiting_admin_action',
  CONFIRMED = 'confirmed',
  READY_FOR_PICKUP = 'ready_for_pickup',
  READY_FOR_DELIVERY = 'ready_for_delivery',
  OUT_FOR_DELIVERY = 'out_for_delivery',
  DELIVERED = 'delivered',
  PICKED_UP = 'picked_up',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
}

export enum FulfillmentMethod {
  PICKUP = 'pickup',
  DELIVERY = 'delivery',
}

export enum OrderHistoryAction {
  ORDER_CREATED = 'order_created',
  ORDER_UPDATED = 'order_updated',
  PAYMENT_RECEIVED = 'payment_received',
  PAYMENT_PARTIALLY_RECEIVED = 'payment_partially_received',
  PAYMENT_STATUS_CHANGED = 'payment_status_changed',
  FULFILLMENT_STARTED = 'fulfillment_started',
  FULFILLMENT_STATUS_CHANGED = 'fulfillment_status_changed',
  ITEM_TRANSFERRED = 'item_transferred',
  TRANSFER_ISSUE = 'transfer_issue',
  READY_FOR_PICKUP = 'ready_for_pickup',
  READY_FOR_DELIVERY = 'ready_for_delivery',
  OUT_FOR_DELIVERY = 'out_for_delivery',
  DELIVERED = 'delivered',
  PICKED_UP = 'picked_up',
  ORDER_COMPLETED = 'order_completed',
  ORDER_CANCELLED = 'order_cancelled',
  ORDER_EXPIRED = 'order_expired',
  REFUND_ISSUED = 'refund_issued',
  REFUND_PARTIAL = 'refund_partial',
  REFUND_FULL = 'refund_full',
  DELIVERY_ADDRESS_UPDATED = 'delivery_address_updated',
  B2B_APPROVAL_PENDING = 'b2b_approval_pending',
  B2B_APPROVAL_APPROVED = 'b2b_approval_approved',
  B2B_APPROVAL_REJECTED = 'b2b_approval_rejected',
}

/** Planned transfer for order fulfillment */
export interface OrderPlannedTransfer {
  productId: string;
  productName: string;
  quantity: number;
  fromLocationId: string;
  fromLocationName: string;
  toLocationId: string;
  toLocationName: string;
}

/** Delivery address for an order */
export interface OrderDeliveryAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  notes?: string;
}

/** Order item with pricing breakdown */
export interface OrderItem {
  id: string;
  orderId: string;
  order?: Order;
  productId: string;
  product?: Product;
  productName: string;
  productAttributes?: string;
  quantity: number;
  unitPrice: number;
  subtotal: number;
  unitCost?: number | null;
  totalCost?: number | null;
  margin?: number | null;
  // B2B pricing breakdown
  retailPrice?: number | null;
  b2bBasePrice?: number | null;
  tierDiscountAmount?: number | null;
  quantityDiscountAmount?: number | null;
  finalUnitPrice?: number | null;
  createdAt: string;
  updatedAt: string;
}

/** Unified Order entity (replaces Reservation for order management) */
export interface Order {
  id: string;
  trackingNumber: string;
  organizationId?: string;
  customerId?: string;
  customer?: User;
  cartId?: string;
  cart?: Cart;
  locationId: string;
  location?: Location;
  staffId?: string;
  staff?: User;
  // Order type
  orderType: OrderType;
  // Status tracking
  paymentStatus: OrderPaymentStatus;
  fulfillmentStatus: OrderFulfillmentStatus;
  // B2B fields
  quoteId?: string;
  resellerId?: string;
  reseller?: {
    id: string;
    companyName?: string;
    user?: User;
  };
  pricingTierId?: string;
  pricingTier?: {
    id: string;
    tierName: string;
  };
  b2bMetadata?: Record<string, unknown>;
  // Transfer tracking
  plannedTransfers?: OrderPlannedTransfer[];
  pendingTransferIds?: string[];
  // Delivery information
  deliveryId?: string;
  delivery?: Delivery;
  deliveryAddress?: OrderDeliveryAddress | null;
  fulfillmentMethod: FulfillmentMethod;
  // Financial fields
  subtotal: number;
  tax: number;
  discount: number;
  totalAmount: number;
  totalItems: number;
  // Payment information
  paymentMethod?: PaymentMethod | null;
  /** Cumulative amount paid towards this order */
  paidAmount: number;
  refundedAmount: number;
  refundedAt?: string;
  isRefunded: boolean;
  // Receipt information
  receiptNumber?: string;
  completedAt?: string;
  // Customer information
  customerEmail?: string;
  customerPhone?: string;
  customerName?: string;
  // Order details
  notes?: string;
  // Reservation/pickup fields
  expiresAt?: string;
  requiresPickup: boolean;
  pickupScheduledAt?: string;
  pickupAddress?: string;
  notificationSent: boolean;
  pickedUpAt?: string;
  // Items relation
  items?: OrderItem[];
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

/** Order history entry for audit trail */
export interface OrderHistory {
  id: string;
  orderId?: string;
  order?: Order;
  action: OrderHistoryAction;
  performedById?: string;
  performedBy?: User;
  metadata?: Record<string, unknown>;
  notes?: string;
  createdAt: string;
}

/** Sort fields for order queries */
export enum OrdersSortField {
  CREATED_AT = 'createdAt',
  TOTAL_AMOUNT = 'totalAmount',
  TRACKING_NUMBER = 'trackingNumber',
  RECEIPT_NUMBER = 'receiptNumber',
  EXPIRES_AT = 'expiresAt',
}

/** Query parameters for fetching orders from API */
export interface OrderQueryParams {
  // Pagination
  page?: number;
  limit?: number;
  // Order type filters
  orderType?: OrderType;
  orderTypes?: OrderType[];
  // Payment status filters
  paymentStatus?: OrderPaymentStatus;
  paymentStatuses?: OrderPaymentStatus[];
  // Fulfillment status filters
  fulfillmentStatus?: OrderFulfillmentStatus;
  fulfillmentStatuses?: OrderFulfillmentStatus[];
  // Organization filter
  organizationId?: string;
  // Location filter
  locationId?: string;
  // Staff filter
  staffId?: string;
  // Customer/Reseller filters
  customerId?: string;
  resellerId?: string;
  // Date range filters
  startDate?: string;
  endDate?: string;
  // Payment method filter
  paymentMethod?: PaymentMethod;
  // Amount filters
  minTotal?: number;
  maxTotal?: number;
  // Search
  trackingNumber?: string;
  receiptNumber?: string;
  search?: string;
  // Refund filter
  isRefunded?: boolean;
  // Expiration filter
  isExpired?: boolean;
  // Sorting
  sortBy?: OrdersSortField;
  sortOrder?: 'ASC' | 'DESC';
  // Include related data
  includeItems?: boolean;
  includeCustomer?: boolean;
  includeStaff?: boolean;
  includeHistory?: boolean;
  includeReseller?: boolean;
  includeLocation?: boolean;
  includeOrganization?: boolean;
}

/** Payload for creating an order item */
export interface OrderItemCreatePayload {
  productId: string;
  quantity: number;
}

/** Payload for creating a new order */
export interface OrderCreatePayload {
  // Required fields
  orderType: OrderType;
  locationId: string;
  items: OrderItemCreatePayload[];
  fulfillmentMethod: FulfillmentMethod;
  // Customer information (optional for guest orders)
  customerId?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerName?: string;
  // Staff processing the order (optional)
  staffId?: string;
  // Cart reference (optional, if order is created from cart)
  cartId?: string;
  // B2B-specific fields
  quoteId?: string;
  resellerId?: string;
  pricingTierId?: string;
  b2bMetadata?: Record<string, unknown>;
  // Delivery address (required if fulfillmentMethod is DELIVERY)
  deliveryAddress?: OrderDeliveryAddress;
  pickupAddress?: string;
  // Financial fields
  tax?: number;
  discount?: number;
  // Payment information (optional at order creation)
  paymentMethod?: PaymentMethod;
  // Reservation/scheduling fields
  expiresAt?: string;
  requiresPickup?: boolean;
  pickupScheduledAt?: string;
  // Order notes
  notes?: string;
}

export interface Sale {
  id: string;
  customerId?: string;
  customer?: User;
  locationId: string;
  location?: Location;
  items: SaleItem[];
  totalAmount: number;
  paymentMethod: 'cash' | 'card' | 'mobile' | 'other';
  status: 'completed' | 'refunded' | 'cancelled';
  notes?: string;
  createdAt: string;
  updatedAt: string;

  // B2B fields
  saleType?: SaleType;
  resellerId?: string;
  reseller?: {
    id: string;
    companyName?: string;
    user?: User;
  };
  pricingTierId?: string;
  pricingTier?: {
    id: string;
    tierName: string;
  };
  b2bMetadata?: {
    poNumber?: string;
    paymentTerms?: string;
    pricingSummary?: Record<string, unknown>;
  };

  // Backend field names
  subtotal?: number;
  total?: number;
  receiptNumber?: string;
  isRefunded?: boolean;
  refundedAmount?: number;
}

export interface SaleItem {
  id: string;
  saleId: string;
  productId: string;
  productName?: string;
  product?: Product;
  quantity: number;
  unitPrice?: number;
  price?: number;
  subtotal?: number;
  discount?: number;

  // B2B pricing breakdown
  retailPrice?: number;
  b2bBasePrice?: number;
  tierDiscountAmount?: number;
  quantityDiscountAmount?: number;
  finalUnitPrice?: number;
}

export interface DashboardStats {
  todaySales: number;
  weekSales: number;
  monthSales: number;
  lowStockProducts: number;
  pendingReservations: number;
  totalProducts: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page?: number;
  pageSize?: number;
}

// Inventory Movement Types
export type InventoryMovementType =
  | 'sale'
  | 'sale_return'
  | 'reservation'
  | 'reservation_cancel'
  | 'reservation_complete'
  | 'transfer_out'
  | 'transfer_in'
  | 'transfer_allocate'
  | 'transfer_deallocate'
  | 'adjustment_add'
  | 'adjustment_remove'
  | 'restock'
  | 'loss'
  | 'count'
  | 'initial'
  | 'delete';

export interface InventoryMovement {
  id: string;
  organizationId: string;
  inventoryId: string;
  productId: string;
  product?: Product;
  locationId: string;
  location?: Location;
  movementType: InventoryMovementType;
  quantityChange: number;
  quantityBefore: number;
  quantityAfter: number;
  reservedBefore: number;
  reservedAfter: number;
  allocatedBefore: number;
  allocatedAfter: number;
  referenceId?: string;
  referenceType?: string;
  performedById?: string;
  performedBy?: User;
  reason?: string;
  notes?: string;
  unitCost?: number;
  totalCost?: number;
  metadata?: Record<string, any>;
  createdAt: string;
}

export interface StockForecast {
  productId: string;
  productName: string;
  currentStock: number;
  averageDailySales: number;
  daysUntilStockout: number | null;
  recommendedReorderQuantity: number;
  reorderPoint: number;
  salesTrend: 'increasing' | 'decreasing' | 'stable';
  lastSaleDate: string | null;
}

export interface MovementSummary {
  totalIn: number;
  totalOut: number;
  netChange: number;
  byType: Record<InventoryMovementType, number>;
}

export interface LocationStats {
  totalProducts: number;
  totalQuantity: number;
  totalReserved: number;
  totalAllocated: number;
  totalAvailable: number;
  lowStockCount: number;
  outOfStockCount: number;
  totalValue: number;
}

export interface Inventory {
  id: string;
  productId: string;
  product?: Product;
  locationId: string;
  location?: Location;
  organizationId: string;
  quantity: number;
  reserved: number;
  allocated: number;
  lowStockThreshold: number;
  storageLocation?: string;
  lastCountedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Location Types (unified Store and Warehouse)
export type LocationType = 'store' | 'warehouse';
export type Currency = 'DZD' | 'EUR' | 'USD' | 'GBP' | 'CHF' | 'MAD' | 'TND';

export interface Location {
  id: string;
  organizationId: string;
  name: string;
  slug?: string;
  code?: string;
  type: LocationType;
  description?: string;
  address?: string;
  city?: string;
  wilaya?: string;
  state?: string;
  zipCode?: string;
  country: string;
  latitude?: number;
  longitude?: number;
  currency?: Currency;
  phone?: string;
  email?: string;
  logo?: string;
  // Capability flags
  canSellToCustomers: boolean;
  canFulfillOnlineOrders: boolean;
  canReceiveTransfers: boolean;
  canSendTransfers: boolean;
  managerId?: string;
  manager?: User;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Deprecated - use Location instead
/** @deprecated Use Location with type='store' instead */
export type Store = Location;
/** @deprecated Use Location with type='warehouse' instead */
export type Warehouse = Location;
/** @deprecated Use LocationType instead */
export type StoreType = LocationType;
/** @deprecated Use LocationType instead */
export type WarehouseType = LocationType;

// Stock Transfer Types
export type StockTransferStatus = 'pending' | 'approved' | 'in_transit' | 'partially_completed' | 'completed' | 'cancelled';

export type StockTransferItemStatus = 'pending' | 'shipped' | 'received' | 'cancelled';

export interface StockTransferItem {
  id: string;
  stockTransferId: string;
  productId: string;
  product?: Product;
  quantity: number;
  quantityShipped: number;
  quantityReceived: number;
  status: StockTransferItemStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface StockTransfer {
  id: string;
  organizationId: string;
  // Single-product fields (legacy, optional for backwards compatibility)
  productId?: string;
  product?: Product;
  quantity?: number;
  // Multi-product fields
  isMultiProduct: boolean;
  items?: StockTransferItem[];
  fromLocationId: string;
  fromLocation?: Location;
  toLocationId: string;
  toLocation?: Location;
  status: StockTransferStatus;
  trackingNumber?: string;
  carrier?: string;
  notes?: string;
  requestedById: string;
  requestedBy?: User;
  approvedById?: string;
  approvedBy?: User;
  completedById?: string;
  completedBy?: User;
  requestedAt: string;
  approvedAt?: string;
  shippedAt?: string;
  completedAt?: string;
  cancelledAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Product Inventory across locations
export interface ProductInventorySummary {
  productId: string;
  productName: string;
  productSku: string;
  totalQuantity: number;
  totalReserved: number;
  totalAllocated: number;
  totalAvailable: number;
  locations: {
    locationId: string;
    locationName: string;
    locationType: LocationType;
    quantity: number;
    reserved: number;
    allocated: number;
    available: number;
    lowStockThreshold: number;
    isLowStock: boolean;
  }[];
}

// Permission Types
export type PermissionResource =
  | 'products'
  | 'inventory'
  | 'sales'
  | 'reservations'
  | 'staff'
  | 'locations'
  | 'reports'
  | 'settings'
  | 'customers'
  | 'b2b'
  | 'deliveries'
  | 'financials'
  | 'cash_drawer'
  | 'credit';

export type PermissionAction = 'read' | 'create' | 'update' | 'delete';

export interface Permission {
  id: string;
  resource: PermissionResource;
  action: PermissionAction;
  description?: string;
}

export interface RolePermission {
  id: string;
  roleId: string;
  permissionId: string;
  permission?: Permission;
}

export interface Role {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  isSystemDefault: boolean;
  systemKey?: string;
  hierarchyLevel: number; // Level 0 (Owner), Level 1 (Admin), Level 2 (Manager), Level 3 (Staff)
  /** Admin flag - users with this role bypass permission checks within their org */
  isAdmin?: boolean;
  /** Protected roles cannot be edited or deleted (e.g., 'owner' role) */
  isSystemProtected?: boolean;
  /** Role category for grouping: administrative, operational, or combined */
  category?: RoleCategory;
  rolePermissions?: RolePermission[];
  createdAt: string;
  updatedAt: string;
}

export interface UserLocation {
  id: string;
  userId: string;
  locationId: string;
  location?: Location;
}

export interface CreateRoleDto {
  name: string;
  description?: string;
  hierarchyLevel?: number;
  permissionIds: string[];
}

export interface UpdateRoleDto {
  name?: string;
  description?: string;
  hierarchyLevel?: number;
  permissionIds?: string[];
}

// Delivery Types
export type DeliveryStatus =
  | 'pending'
  | 'assigned'
  | 'picked_up'
  | 'out_for_delivery'
  | 'delivered'
  | 'failed'
  | 'cancelled';

export interface DeliveryAddress {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  notes?: string;
}

export interface ProofOfDelivery {
  type: 'signature' | 'photo' | 'code';
  value: string;
  capturedAt: string;
}

export interface Delivery {
  id: string;
  organizationId: string;
  reservationId: string;
  reservation?: Reservation;
  status: DeliveryStatus;
  pickupLocationId: string;
  pickupLocation?: Location;
  driverId?: string;
  driver?: User;
  deliveryAddress: DeliveryAddress;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
  assignedAt?: string;
  pickedUpAt?: string;
  outForDeliveryAt?: string;
  deliveredAt?: string;
  failedAt?: string;
  cancelledAt?: string;
  estimatedDeliveryTime?: string;
  notes?: string;
  driverNotes?: string;
  failureReason?: string;
  proofOfDelivery?: ProofOfDelivery;
  createdAt: string;
  updatedAt: string;
}

// Supplier Types
export type PaymentTerms =
  | 'net_7'
  | 'net_15'
  | 'net_30'
  | 'net_60'
  | 'net_90'
  | 'due_on_receipt'
  | 'custom';

export interface Supplier {
  id: string;
  organizationId: string;
  name: string;
  code: string;
  contactName?: string;
  phone?: string;
  email?: string;
  address?: string;
  defaultPaymentTerms: PaymentTerms;
  currency: string;
  taxId?: string;
  notes?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

export interface CreateSupplierDto {
  name: string;
  code: string;
  contactName?: string;
  phone?: string;
  email?: string;
  address?: string;
  defaultPaymentTerms?: PaymentTerms;
  currency?: string;
  taxId?: string;
  notes?: string;
  isActive?: boolean;
}

export interface UpdateSupplierDto extends Partial<CreateSupplierDto> {}

// Product Supplier Price Types
export type PriceSource = 'QUOTE' | 'RESTOCK';

export interface ProductSupplierPrice {
  id: string;
  organizationId: string;
  productId: string;
  supplierId: string;
  supplier?: Supplier;
  unitCost: number;
  minimumQuantity?: number;
  maximumQuantity?: number;
  currency: string;
  source: PriceSource;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
  isPreferred: boolean;
  lastPurchaseDate?: string;
  lastPurchaseQuantity?: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductSupplierPriceDto {
  supplierId: string;
  unitCost: number;
  minimumQuantity?: number;
  currency?: string;
  isPreferred?: boolean;
}

export interface UpdateProductSupplierPriceDto extends Partial<CreateProductSupplierPriceDto> {}

// Inventory Batch Types
export interface InventoryBatch {
  id: string;
  organizationId: string;
  productId: string;
  locationId: string;
  location?: Location;
  batchNumber: string;
  supplierId?: string;
  supplier?: Supplier;
  quantityReceived: number;
  quantityRemaining: number;
  unitCost: number;
  receivedDate: string;
  isActive: boolean;
  createdAt: string;
}

// Restock With Supplier DTO
export interface RestockWithSupplierDto {
  productId: string;
  locationId: string;
  quantity: number;
  supplierId: string;
  unitCost: number;
  batchNumber?: string;
  notes?: string;
}

export interface RestockWithSupplierResponse {
  inventory: {
    id: string;
    productId: string;
    locationId: string;
    quantity: number;
  };
  movement: {
    id: string;
    movementType: string;
    quantityChange: number;
    unitCost: number;
    totalCost: number;
  };
  batch?: InventoryBatch;
}

// ============================================
// Restock Types (Purchase Orders)
// ============================================

export enum RestockStatus {
  QUOTE = 'quote',
  ORDERED = 'ordered',
  DELIVERED = 'delivered',
  PAID = 'paid',
  CANCELLED = 'cancelled',
}

/** History action types for restock operations */
export enum RestockHistoryAction {
  RESTOCK_CREATED = 'restock_created',
  RESTOCK_UPDATED = 'restock_updated',
  ITEMS_ADDED = 'items_added',
  ITEMS_REMOVED = 'items_removed',
  STATUS_CONFIRMED = 'status_confirmed',
  STATUS_RECEIVED = 'status_received',
  STATUS_PAID = 'status_paid',
  STATUS_CANCELLED = 'status_cancelled',
  RESTOCK_DUPLICATED = 'restock_duplicated',
}

/** Line item for a restock order */
export interface RestockItem {
  id: string;
  restockId: string;
  restock?: Restock;
  productId: string;
  product?: Product;
  /** Snapshot of product name at time of order */
  productName: string;
  quantity: number;
  unitCost: number;
  subtotal: number;
  /** Inventory batch ID (if batch tracking enabled) */
  inventoryBatchId?: string;
  /** Batch number (if batch tracking enabled) */
  batchNumber?: string;
  createdAt: string;
  updatedAt: string;
}

/** @deprecated Use RestockItem instead */
export type BulkRestockOrderItem = RestockItem;

/** History entry for a restock operation */
export interface RestockHistory {
  id: string;
  restockId: string;
  restock?: Restock;
  action: RestockHistoryAction;
  performedById?: string;
  performedBy?: User;
  /** Additional context about the action */
  metadata?: Record<string, unknown>;
  notes?: string;
  createdAt: string;
}

/** Restock order (supplier purchase order) */
export interface Restock {
  id: string;
  organizationId: string;
  supplierId: string;
  supplier?: Supplier;
  locationId: string;
  location?: Location;
  /** Unique order number (e.g., PO-XXXXXX) */
  orderNumber: string;
  status: RestockStatus;
  /** Sum of line item totals */
  subtotal: number;
  /** Tax amount (optional) */
  tax: number;
  /** Final total */
  totalAmount: number;
  notes?: string;
  /** Expected delivery date */
  expectedDeliveryDate?: string;
  /** Timestamp when moved to ORDERED */
  orderedAt?: string;
  /** Timestamp when moved to DELIVERED */
  deliveredAt?: string;
  /** Timestamp when moved to PAID */
  paidAt?: string;
  /** Last status change timestamp */
  statusChangedAt?: string;
  /** User who changed the status */
  statusChangedById?: string;
  statusChangedBy?: User;
  createdById?: string;
  createdBy?: User;
  items?: RestockItem[];
  history?: RestockHistory[];
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
}

/** @deprecated Use Restock instead */
export type BulkRestockOrder = Restock;

// Product Type Enum (Phase 1.1)
export type ProductType = 'sellable' | 'internal';

export const ProductTypeLabels: Record<ProductType, string> = {
  sellable: 'Vendable',
  internal: 'Interne',
};

// Cart Types (Phase 2: Multi-Session POS)
export type CartStatus = 'active' | 'reserved' | 'completed' | 'abandoned';

export interface CartItem {
  id: string;
  cartId: string;
  productId: string;
  product?: Product;
  quantity: number;
  price: number;
  subtotal: number;
  createdAt: string;
  updatedAt: string;
}

export interface Cart {
  id: string;
  organizationId?: string;
  customerId?: string;
  customer?: User;
  sessionId?: string;
  status: CartStatus;
  items: CartItem[];
  totalAmount: number;
  totalItems: number;
  // Suspend fields (Phase 2)
  isSuspended?: boolean;
  suspendedAt?: string;
  suspendedBy?: string;
  customerName?: string;
  suspensionNotes?: string;
  lastActivityAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface SuspendCartDto {
  customerName?: string;
  notes?: string;
}

// Barcode Search Response
export interface BarcodeSearchResponse {
  found: boolean;
  product?: Product & {
    inventoryAtLocation?: Inventory;
  };
  message?: string;
}

// ============================================
// Expense Management Types
// ============================================

export type ExpenseCategory =
  | 'restock'
  | 'salary'
  | 'consumable'
  | 'utilities'
  | 'rent'
  | 'maintenance'
  | 'transport'
  | 'other';

export const ExpenseCategoryLabels: Record<ExpenseCategory, string> = {
  restock: 'Réapprovisionnement',
  salary: 'Salaires',
  consumable: 'Consommables',
  utilities: 'Charges',
  rent: 'Loyer',
  maintenance: 'Maintenance',
  transport: 'Transport',
  other: 'Autre',
};

export type RecurringFrequency = 'weekly' | 'biweekly' | 'monthly';

export const RecurringFrequencyLabels: Record<RecurringFrequency, string> = {
  weekly: 'Hebdomadaire',
  biweekly: 'Bimensuel',
  monthly: 'Mensuel',
};

export type PaymentMethod = 'cash' | 'card' | 'mobile' | 'other';

export const PaymentMethodLabels: Record<PaymentMethod, string> = {
  cash: 'Espèces',
  card: 'Carte',
  mobile: 'Mobile',
  other: 'Autre',
};

export interface ExpenseReceipt {
  id: string;
  fileName: string;
  filePath: string;
  mimeType: string;
  uploadedAt: string;
  uploadedById: string;
}

export interface Expense {
  id: string;
  organizationId: string;
  category: ExpenseCategory;
  amount: number;
  expenseDate: string;
  description?: string;
  locationId?: string;
  location?: Location;
  supplierId?: string;
  supplier?: Supplier;
  staffId?: string;
  staff?: User;
  inventoryMovementId?: string;
  paymentMethod: PaymentMethod;
  receipts?: ExpenseReceipt[];
  referenceNumber?: string;
  isRecurring: boolean;
  recurringFrequency?: RecurringFrequency;
  recordedById: string;
  recordedBy?: User;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExpenseDto {
  category: ExpenseCategory;
  amount: number;
  expenseDate: string;
  description?: string;
  locationId?: string;
  supplierId?: string;
  staffId?: string;
  paymentMethod?: PaymentMethod;
  referenceNumber?: string;
  isRecurring?: boolean;
  recurringFrequency?: RecurringFrequency;
  metadata?: Record<string, unknown>;
}

export interface UpdateExpenseDto extends Partial<CreateExpenseDto> {}

export interface QueryExpensesDto {
  page?: number;
  limit?: number;
  category?: ExpenseCategory;
  locationId?: string;
  supplierId?: string;
  staffId?: string;
  startDate?: string;
  endDate?: string;
  minAmount?: number;
  maxAmount?: number;
  paymentMethod?: PaymentMethod;
  isRecurring?: boolean;
  search?: string;
  sortBy?: 'expenseDate' | 'amount' | 'category' | 'createdAt';
  sortOrder?: 'ASC' | 'DESC';
}

// Staff Salary Config Types
export interface StaffSalaryConfig {
  id: string;
  organizationId: string;
  userId: string;
  user?: User;
  baseSalary: number;
  paymentFrequency: RecurringFrequency;
  effectiveDate: string;
  endDate?: string;
  locationId?: string;
  location?: Location;
  metadata?: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStaffSalaryConfigDto {
  userId: string;
  baseSalary: number;
  paymentFrequency?: RecurringFrequency;
  effectiveDate: string;
  locationId?: string;
  metadata?: Record<string, unknown>;
}

export interface UpdateStaffSalaryConfigDto extends Partial<CreateStaffSalaryConfigDto> {
  endDate?: string;
}

// Expense Analytics Types
export interface ExpenseAnalyticsSummary {
  totalAmount: number;
  expenseCount: number;
  averageAmount: number;
  byCategory: Record<ExpenseCategory, number>;
  byPaymentMethod: Record<PaymentMethod, number>;
}

export interface ExpenseByCategoryItem {
  category: ExpenseCategory;
  totalAmount: number;
  count: number;
  percentage: number;
}

export interface ExpenseByPeriodItem {
  period: string;
  totalAmount: number;
  count: number;
}

// Salary Generation Types
export interface SalaryPreviewItem {
  userId: string;
  userName: string;
  baseSalary: number;
  effectiveDate: string;
  daysInPeriod: number;
  daysWorked: number;
  proratedAmount: number;
  alreadyGenerated: boolean;
}

export interface GenerateSalariesDto {
  year: number;
  month: number;
  locationId?: string;
  paymentMethod?: PaymentMethod;
}

export interface GenerateSalariesResponse {
  generated: number;
  skipped: number;
  expenseIds: string[];
}

// ============================================
// Customer History Types
// ============================================

export interface CustomerNote {
  id: string;
  customerId: string;
  staffId: string;
  staff?: User;
  note: string;
  isInternal: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LoyaltyAccount {
  id: string;
  customerId: string;
  points: number;
  lifetimePoints: number;
  tier?: string;
  createdAt: string;
  updatedAt: string;
}

// Import Quote, Invoice from types/b2b.ts when using this interface
export interface CustomerHistoryData {
  orders: Order[];
  sales: Sale[];
  reservations: Reservation[];
  quotes: import('./b2b').Quote[];
  invoices: import('./b2b').Invoice[];
  deliveries: Delivery[];
  loyalty?: LoyaltyAccount;
  notes: CustomerNote[];
  lifetimeValue: number;
  totalOrders: number;
  totalSpent: number;
}
